import { cva } from 'class-variance-authority';

/**
 * Checkbox class recipes.
 *
 * Like `Button.recipe` and `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-checkbox-*` slots that `.ds-checkbox` sets per theme in
 * `components/styles/design-system.css`.
 *
 * ── The geometry, and why it is what it is ─────────────────────────────────────────────────────
 *
 * All 24 cells of Figma node 585:57 share one shape, read off the exported vector rather than
 * eyeballed:
 *
 *   a 24x24 area, `rx 8`            the hit target, and the focus halo
 *   a `rect` at 3.75, 16.5 square   with a 1.5px CENTRED stroke, so it spans 3 -> 21
 *   `rx 5.25` on that rect          measured on the stroke's centreline
 *
 * A centred SVG stroke is half in and half out, which is why the numbers look odd against CSS. Both
 * translate exactly: an 18px border-box element inset 3px on every side has the same outer bounds,
 * and the outer radius is the authored 5.25 plus half the stroke — 6px.
 *
 * ── Nothing moves, in any state ────────────────────────────────────────────────────────────────
 *
 * The border is 1.5px in every cell, so unlike the text input there is no stroke to thicken. The
 * focus treatment is a BACKGROUND on the 24px parent rather than an outline or a ring, which means
 * focus costs no layout either — see `NothingMovesOnFocus` in the stories.
 *
 * It also has to be a background rather than an outline for a second reason: Figma draws the halo
 * BEHIND the box, and the unchecked box is unfilled while focused, so the halo shows through its
 * interior. That is the whole visual in dark mode — a blue square with a blue stroke.
 *
 * ── How the states are selected ────────────────────────────────────────────────────────────────
 *
 * `group/cb` sits on `Checkbox.Root`, which is where Base UI puts `data-checked`, `data-unchecked`,
 * `data-indeterminate` and `data-disabled`, and which is also the focusable element. So one group
 * serves the whole component and there is no React state behind any of the colour changes.
 *
 * The pivot is `data-unchecked`, which Base UI sets ONLY when the box is neither ticked nor
 * indeterminate. `:not([data-unchecked])` therefore means "showing a glyph" in one selector instead
 * of matching `data-checked` and `data-indeterminate` separately. `IndeterminateHasNeitherChecked-
 * NorUnchecked` in the tests guards that premise, since the whole recipe rests on it.
 *
 * Every state selector is written to be MUTUALLY EXCLUSIVE rather than relying on utility sort
 * order, the same discipline the other two recipes use. Precedence, highest first:
 *
 *   disabled -> focus-visible -> hover -> resting
 *
 * with one deliberate exception: on a ticked box, hover beats focus. The design gives ticked cells
 * no focus colour of their own (they stay Blue/Primary and gain only the halo), so hover is the only
 * thing left that can change them.
 */

/**
 * The wrapper, which is a real `<label>`.
 *
 * `cursor` is set here and inherited by everything inside, so the box and the text agree — a
 * checkbox whose label is a text cursor and whose box is a pointer looks broken.
 *
 * `items-start`, not `items-center`: a label long enough to wrap must leave the box on the first
 * line rather than dragging it down to the centre of the whole text block. See the note on
 * `checkboxLabelRecipe` for the 2px that keeps a single line looking exactly as it did.
 */
export const checkboxRootRecipe = cva(['ds-checkbox', 'inline-flex w-fit items-start gap-[8px] py-[2px]'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * `Checkbox.Root` — the 24px hit area, the focus halo, and the group every other recipe reads.
 *
 * `outline-none` replaces the UA focus ring with the halo. That is only defensible because focus
 * changes the box's STROKE to Blue/Primary as well: the halo is a 20%/50% tint and would not carry
 * WCAG 1.4.11 on its own, whereas a stroke changing from neutral to primary does.
 *
 * `focus-visible` rather than `focus`, so the halo answers a keyboard tab and not a mouse click.
 * Figma's Focused cell does not say which it means; showing it on click would put a halo on every
 * box the user merely toggles.
 */
export const checkboxControlRecipe = cva([
  'group/cb',
  'relative block size-[24px] shrink-0 cursor-[inherit] rounded-[8px]',
  'bg-transparent outline-none',
  'focus-visible:bg-[var(--ds-checkbox-halo)]',
  'transition-colors duration-150 motion-reduce:transition-none',
]);

/**
 * The box itself — a child rather than the root, because the halo needs a larger radius (8px) than
 * the box (6px) and one element cannot have both.
 *
 * `pointer-events-none` so it can never intercept a click that belongs to the root above it.
 */
export const checkboxBoxRecipe = cva([
  'pointer-events-none absolute inset-[3px] rounded-[6px]',
  /**
   * `length:` disambiguates a width from a colour, which is how the sibling recipes write it too.
   *
   * The 1.5px is authored and reproduced, but worth knowing: a browser snaps a border's USED width
   * to whole device pixels, so on a DPR-1 display this paints as 1px and only Retina gets the extra
   * half. Figma's own 1.5px stroke has exactly the same fate, so this is faithful rather than a
   * rounding bug — but it is why `Geometry` cannot assert the width as a literal.
   */
  'border-[length:1.5px] border-solid',
  'transition-[border-color,background-color] duration-150 motion-reduce:transition-none',

  // Unchecked, resting. The base case, so it needs no selector — everything below out-specifies it.
  'border-[var(--ds-checkbox-border)] bg-[var(--ds-checkbox-bg)]',

  // Unchecked, hovered. The stroke is unchanged in both themes; only the fill warms up.
  'group-[[data-unchecked]:not([data-disabled]):not(:focus-visible):hover]/cb:bg-[var(--ds-checkbox-bg-hover)]',

  // Unchecked, focused. The fill is dropped so the halo behind reads through the box.
  'group-[[data-unchecked]:not([data-disabled]):focus-visible]/cb:border-[var(--ds-checkbox-border-focus)]',
  'group-[[data-unchecked]:not([data-disabled]):focus-visible]/cb:bg-transparent',

  // Unchecked, disabled. An outline only — no fill in either theme.
  'group-[[data-unchecked][data-disabled]]/cb:border-[var(--ds-checkbox-border-disabled)]',
  'group-[[data-unchecked][data-disabled]]/cb:bg-transparent',

  // Ticked or indeterminate, resting or focused. Fill and stroke are the same value.
  'group-[:not([data-unchecked]):not([data-disabled]):not(:hover)]/cb:border-[var(--ds-checkbox-on)]',
  'group-[:not([data-unchecked]):not([data-disabled]):not(:hover)]/cb:bg-[var(--ds-checkbox-on)]',

  // Ticked or indeterminate, hovered — a step down the blue ramp, and the one state that outranks
  // focus (see the header).
  'group-[:not([data-unchecked]):not([data-disabled]):hover]/cb:border-[var(--ds-checkbox-on-hover)]',
  'group-[:not([data-unchecked]):not([data-disabled]):hover]/cb:bg-[var(--ds-checkbox-on-hover)]',

  // Ticked or indeterminate, disabled.
  'group-[:not([data-unchecked])[data-disabled]]/cb:border-[var(--ds-checkbox-on-disabled)]',
  'group-[:not([data-unchecked])[data-disabled]]/cb:bg-[var(--ds-checkbox-on-disabled)]',
]);

/**
 * `Checkbox.Indicator` — the glyph layer, mounted only while there is a glyph to draw.
 *
 * Sized to the full 24px rather than to the 18px box, so the paths inside can be Figma's own
 * coordinates in Figma's own viewBox with no arithmetic in between.
 */
export const checkboxGlyphRecipe = cva([
  'pointer-events-none absolute inset-0 block',
  'text-[var(--ds-checkbox-glyph)]',
  'group-data-[disabled]/cb:text-[var(--ds-checkbox-glyph-disabled)]',
  '[&_svg]:size-full',
]);

/**
 * The label text.
 *
 * `py-[2px]` is what makes `items-start` on the wrapper safe. The text is 14px on a 20px line and
 * the box is 24px, so top-aligning the two raw would sit the first line 2px above the box's optical
 * centre. 2px of padding grows the FIRST LINE BOX to 24px, which centres it against the box and
 * leaves a single-line checkbox rendering identically to the `items-center` version it replaces —
 * the only thing that changes is where the box sits once the text wraps. `LongLabel` measures both
 * halves of that claim.
 *
 * Both colours are variants rather than a base plus an override: two bare `text-*` utilities have
 * the same specificity, so which one won would come down to Tailwind's output order rather than
 * anything written here.
 */
export const checkboxLabelRecipe = cva(['font-sans text-body-md select-none py-[2px]'], {
  variants: {
    disabled: {
      true: 'text-[var(--ds-checkbox-label-disabled)]',
      false: 'text-[var(--ds-checkbox-label)]',
    },
  },
  defaultVariants: { disabled: false },
});
